import { dataLayer } from "@repo/analytics/data-layer-utils";
import { getSearchTerm } from "@repo/analytics/functions";
import {
  componentNameConstants,
  dlElementConstants,
  dlEventConstants,
} from "@repo/analytics/data-layer.constants";
import {
  livelandingPageClickTypes,
  livelandingPageComponents,
  livelandingPageEventNames,
  livelandingPageEventTypes,
  livelandingPageJourneyNames,
  livelandingPageNames,
  livelandingPagePropertyTypes,
  livelandingPageReferenceIds,
} from "../constants/livelanding-page.constants.ts";
import { MediaDataLike, PlaylistVideoLike } from "../types/livelanding.types.ts";
import { normalizeCategoryName } from "./playlistCategoryUtils";

const FW_RESET_ACTIVE_ATTR = "data-fw-transform-reset-active";
const FW_RESET_HAD_NO_SCROLL_ATTR = "data-fw-transform-reset-had-no-scroll";

export function applyBodyTransformReset(): void {
  if (typeof document === "undefined") return;

  const rootElement = document.documentElement;
  const isResetAlreadyActive = document.body.getAttribute(FW_RESET_ACTIVE_ATTR) === "true";

  if (!isResetAlreadyActive) {
    const hadNoScroll = rootElement.classList.contains("no-scroll");
    rootElement.setAttribute(FW_RESET_HAD_NO_SCROLL_ATTR, hadNoScroll ? "true" : "false");
  }

  rootElement.classList.add("no-scroll");
  document.body.setAttribute(FW_RESET_ACTIVE_ATTR, "true");
  document.body.classList.add("fw-transform-reset");
}

export function isLive(startedAt: string | null, endedAt: string | null): boolean {
  if (!startedAt) return false;
  const now = new Date();
  const startTime = new Date(startedAt);
  if (!endedAt) return now >= startTime;
  const endTime = new Date(endedAt);
  return now >= startTime && now <= endTime;
}

export function hasScrollableContent(scrollWidth: number, clientWidth: number): boolean {
  return scrollWidth > clientWidth;
}

export function calculateVisibleCardsCount(
  containerWidth: number,
  cardWidth: number,
  productsLength: number,
  isLoading: boolean
): number {
  if (containerWidth === 0) return 0;
  const calculatedCount = Math.floor(containerWidth / cardWidth);
  const maxCount = isLoading ? calculatedCount : Math.min(calculatedCount, productsLength);
  return Math.max(maxCount, isLoading ? 2 : 1);
}

export function timeSinceEnded(endedAt: string | null): string {
  if (!endedAt) return "";
  const endedDate = new Date(endedAt);
  const now = new Date();
  const diffMs = now.valueOf() - endedDate.valueOf();

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);

  if (months > 0) return months === 1 ? "1 month ago" : `${months} months ago`;
  if (days > 0) return days === 1 ? "1 day ago" : `${days} days ago`;
  if (hours > 0) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  if (minutes > 0) return minutes === 1 ? "1 min ago" : `${minutes} mins ago`;
  return "Just now";
}

export function computeEffectiveIsLive(params: {
  isLiveProp: boolean | undefined;
  liveOverride: boolean | undefined;
  startedAt: string | null | undefined;
  endedAt: string | null | undefined;
}): boolean {
  const { isLiveProp, liveOverride, startedAt, endedAt } = params;
  if (typeof isLiveProp === "boolean") return isLiveProp;
  if (typeof liveOverride === "boolean") return liveOverride;
  return isLive(startedAt ?? null, endedAt ?? null);
}

export function getUnifiedMediaCardThumbnail(params: {
  video: {
    video_posters?: Array<{ video_poster_type?: string; url?: string }>;
    trailer?: { thumbnail_url?: string };
  };
  variant: string;
}): string {
  const { video, variant } = params;
  const fromPostersOrTrailer =
    video.video_posters?.find((poster) => poster.video_poster_type === "static")?.url ||
    video.trailer?.thumbnail_url;

  if (fromPostersOrTrailer) return fromPostersOrTrailer;

  return variant === "web"
    ? "https://via.placeholder.com/240x427"
    : "https://via.placeholder.com/300x533";
}

export function getHashtagsForWeb(params: {
  rawHashtags: Array<string | null | undefined>;
  isAllTab: boolean;
  activeCategory: string | null | undefined;
  language: string;
}): string[] {
  const { rawHashtags, isAllTab, activeCategory, language } = params;

  if (!rawHashtags || rawHashtags.length === 0) return [];

  const nonEmpty = rawHashtags.filter(
    (tag): tag is string => typeof tag === "string" && tag.length > 0
  );

  if (nonEmpty.length === 0) return [];

  const firstHashtag = nonEmpty[0]!;

  if (isAllTab) return [firstHashtag];

  if (activeCategory != null && activeCategory !== "") {
    const normalizedActive = normalizeCategoryName(activeCategory, language);
    const matching = nonEmpty.filter(
      (tag) => normalizeCategoryName(tag, language) === normalizedActive
    );
    return matching.length ? matching : [firstHashtag];
  }

  return nonEmpty;
}

export function getSubsectionFromHashtags(hashtags?: string[] | null): string {
  return Array.isArray(hashtags) && hashtags.length > 0 ? hashtags.join(",") : "NA";
}

export function formatDurationFull(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}

export function formatScheduledDate(input: string): string {
  const date = new Date(input);
  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const paddedHour = hours.toString().padStart(2, "0");
  const time = `${paddedHour}:${minutes} ${ampm}`;
  return `${day} ${month} | ${time}`;
}

export function capitalizeFirstLetter(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function selectBestVideoPoster(
  posters:
    | Array<{
        format: string;
        height: number;
        url: string;
      }>
    | undefined
): string | null {
  if (!posters || posters.length === 0) return null;

  const jpgPosters = posters.filter((poster) => poster.format === "jpg");

  if (jpgPosters.length === 0) return null;

  const sortedByHeight = [...jpgPosters].sort((a, b) => a.height - b.height);
  return sortedByHeight[0]?.url || null;
}

export function getThumbnailUrl(video: {
  video_posters?: Array<{ video_poster_type?: string; orientation?: string; url?: string }>;
  trailer?: { thumbnail_url?: string };
}): string {
  return (
    video?.video_posters?.find((poster) => poster?.video_poster_type === "static")?.url ||
    video?.trailer?.thumbnail_url ||
    "https://via.placeholder.com/300x533"
  );
}

export function clearBodyTransformReset(): void {
  if (typeof document === "undefined") return;

  const rootElement = document.documentElement;
  const hadNoScroll = rootElement.getAttribute(FW_RESET_HAD_NO_SCROLL_ATTR) === "true";

  if (!hadNoScroll) {
    rootElement.classList.remove("no-scroll");
  }
  rootElement.removeAttribute(FW_RESET_HAD_NO_SCROLL_ATTR);
  document.body.classList.remove("fw-transform-reset");
  document.body.removeAttribute(FW_RESET_ACTIVE_ATTR);
}

export function releaseHtmlNoScroll(): void {
  if (typeof document === "undefined") return;

  const rootElement = document.documentElement;
  const hadNoScroll = rootElement.getAttribute(FW_RESET_HAD_NO_SCROLL_ATTR) === "true";

  if (!hadNoScroll) {
    rootElement.classList.remove("no-scroll");
  }
}

export const COMMON_SPINNER_CLASS_NAME =
  "h-7 w-7 animate-spin rounded-full border-2 border-white/30 border-t-white";

export function restartBooleanLoader(setter: (value: boolean) => void): void {
  setter(false);

  if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(() => setter(true));
    return;
  }

  setter(true);
}

function getFireworkPlayers(): Record<string, any> | null {
  return ((window as any)?._fwn?.players as Record<string, any>) ?? null;
}

function pauseFireworkPlayer(player: any): void {
  if (!player) return;

  try {
    if (typeof player.pause === "function") {
      player.pause();
      return;
    }

    if (typeof player.stop === "function") {
      player.stop();
    }
  } catch {
    // ignore per-player pause failures
  }
}

function getFireworkPlayerByEncodedId(
  players: Record<string, any> | null,
  encodedId: string
): { key: string; player: any } | null {
  if (!players) return null;

  const directKey = `player-${encodedId}`;
  if (players[directKey]) {
    return { key: directKey, player: players[directKey] };
  }

  if (players[encodedId]) {
    return { key: encodedId, player: players[encodedId] };
  }

  const matchedEntry = Object.entries(players).find(([key]) => key.includes(encodedId));
  if (!matchedEntry) return null;

  const [key, player] = matchedEntry;
  return { key, player };
}

export function triggerPlayerFullscreen(playerName: string, delay: number = 500): void {
  try {
    let attemptCount = 0;
    const maxAttempts = 20;

    const tryFullscreen = () => {
      const players = getFireworkPlayers();
      const targetPlayer = players?.[playerName];

      if (players && targetPlayer && typeof targetPlayer.fullscreen === "function") {
        Object.entries(players).forEach(([name, player]) => {
          if (name === playerName || player === targetPlayer) return;
          pauseFireworkPlayer(player);
        });

        targetPlayer.play?.();
        targetPlayer.fullscreen();
        return;
      }

      attemptCount += 1;
      if (attemptCount < maxAttempts) {
        setTimeout(tryFullscreen, delay);
      }
    };

    setTimeout(tryFullscreen, delay);
  } catch {
    // ignore
  }
}

export function pausePlayerByEncodedId(encodedId: string | undefined, delay: number = 100): void {
  if (!encodedId) return;

  const doPause = () => {
    const players = getFireworkPlayers();
    const matched = getFireworkPlayerByEncodedId(players, encodedId);
    if (!matched?.player) return;

    pauseFireworkPlayer(matched.player);
  };

  if (delay > 0) {
    setTimeout(doPause, delay);
  } else {
    doPause();
  }
}

export function safelyInvokeFireworkPlayer(videoEncodedId: string): void {
  console.log("safelyInvokeFireworkPlayer");
}

export function dismissFireworkPipBeforePlayback(): void {
  console.log("dismissFireworkPipBeforePlayback");
}

export function pausePlayerByName(playerName: string | undefined): void {
  if (!playerName) return;

  try {
    const players = getFireworkPlayers();
    const player = players?.[playerName];
    if (player) {
      pauseFireworkPlayer(player);
      return;
    }

    // Fallback: partial key match
    if (players) {
      const matched = Object.entries(players).find(([key]) => key.includes(playerName));
      if (matched) pauseFireworkPlayer(matched[1]);
    }
  } catch {
    // ignore
  }
}

export function isEventForPlayer(detail: any, encodedId: string | undefined): boolean {
  if (!encodedId) return false;

  const normalize = (value: unknown): string =>
    String(value ?? "")
      .trim()
      .replace(/^player-/, "");

  const expected = normalize(encodedId);
  if (!expected) return false;

  const candidates = [
    detail?.playerName,
    detail?.player_name,
    detail?.name,
    detail?.id,
    detail?.playerId,
    detail?.videoId,
    detail?.video_id,
    detail?.encodedId,
    detail?.encoded_id,
    detail?.player?.name,
    detail?.player?.id,
    detail?.player?.videoId,
  ]
    .filter((value) => value != null)
    .map((value) => normalize(value));

  return candidates.some(
    (candidate) =>
      candidate === expected || candidate.includes(expected) || expected.includes(candidate)
  );
}

export function findScopedVideos(container: HTMLElement | null): HTMLVideoElement[] {
  if (!container) return [];
  return Array.from(container.querySelectorAll("video"));
}

export function findScopedFwPlayers(container: HTMLElement | null): Element[] {
  if (!container) return [];
  return Array.from(container.querySelectorAll("fw-player"));
}

export function handleMouseEnterLiveSessions(params: {
  videoEncodedId: string;
  mediaData: MediaDataLike | null;
  heading: string;
  isLive: boolean;
  hoveredVideos: Set<string>;
  openPlayers: Set<string>;
  container: HTMLElement | null;
}): void {
  const { videoEncodedId, mediaData, isLive, hoveredVideos, openPlayers, container } = params;

  if (!mediaData?.live_streams) return;
  const match = mediaData.live_streams.find(
    (stream) => stream.trailer?.encoded_id === videoEncodedId
  );
  if (!match) return;

  if (isLive) {
    if (openPlayers.has(videoEncodedId)) return;
    if (!hoveredVideos.has(videoEncodedId)) {
      hoveredVideos.add(videoEncodedId);
    }
  }

  const videos = findScopedVideos(container);
  videos.forEach((videoEl) => {
    const videoSrc = videoEl.getAttribute("src") || videoEl.dataset?.src;
    if (!videoSrc) {
      return;
    }
    if (videoSrc === match.trailer?.file_url) {
      videoEl.muted = true;
      void videoEl.play();
    }
  });
}

export function handleMouseLeaveLiveSessions(params: {
  videoEncodedId: string;
  mediaData: MediaDataLike | null;
  heading: string;
  isLive: boolean;
  hoveredVideos: Set<string>;
  container: HTMLElement | null;
}): void {
  const { videoEncodedId, mediaData, isLive, hoveredVideos, container } = params;

  if (!mediaData?.live_streams) return;
  const match = mediaData.live_streams.find(
    (stream) => stream.trailer?.encoded_id === videoEncodedId
  );
  if (!match) return;

  if (isLive) {
    hoveredVideos.delete(videoEncodedId);
  }

  const videos = findScopedVideos(container);
  videos.forEach((videoEl) => {
    const videoSrc = videoEl.getAttribute("src") || videoEl.dataset?.src;
    if (!videoSrc) {
      return;
    }
    if (videoSrc === match.trailer?.file_url) {
      videoEl.pause();
      videoEl.currentTime = 0;
    }
  });
}

export function getVideoElement(id: string): HTMLVideoElement | null {
  if (typeof document === "undefined") return null;
  const element = document.getElementById(id);
  return (element as HTMLVideoElement) || null;
}

export function getOverlayElement(id: string): HTMLElement | null {
  if (typeof document === "undefined") return null;
  const element = document.getElementById(id);
  return element;
}

export function safelyPlayVideo(videoElement: HTMLVideoElement): void {
  videoElement.muted = true;
  const playResult = videoElement.play();
  if (playResult instanceof Promise) {
    void playResult.catch((error: unknown) => {
      console.error("Video play failed", error);
    });
  }
}

export function safelyPauseAndResetVideo(videoElement: HTMLVideoElement): void {
  try {
    videoElement.pause();
    videoElement.currentTime = 0;
  } catch (error) {
    console.error("Video pause/reset failed", error);
  }
}

export function safelyToggleOverlay(overlay: HTMLElement, isVisible: boolean): void {
  overlay.style.opacity = isVisible ? "1" : "0";
  overlay.style.pointerEvents = isVisible ? "auto" : "none";
}

export function handleMouseEnterPlayList(params: {
  videoEncodedId: string;
  title: string;
  sectionId: string;
  findVideoByEncodedId: (id: string) => PlaylistVideoLike | undefined;
  getVideoElement: (id: string) => HTMLVideoElement | null;
  getOverlayElement: (id: string) => HTMLElement | null;
  safelyPlayVideo: (videoElement: HTMLVideoElement) => void;
  safelyToggleOverlay: (overlay: HTMLElement, isVisible: boolean) => void;
}): void {
  const {
    videoEncodedId,
    sectionId,
    getVideoElement,
    getOverlayElement,
    safelyPlayVideo,
    safelyToggleOverlay,
  } = params;

  const videoElement = getVideoElement(`${sectionId}-trailer-${videoEncodedId}`);
  if (videoElement) {
    safelyPlayVideo(videoElement);
  }
  const overlay = getOverlayElement(`${sectionId}-overlay-${videoEncodedId}`);
  if (overlay) {
    safelyToggleOverlay(overlay, false);
  }
}

export function handleMouseLeavePlayList(params: {
  videoEncodedId: string;
  title: string;
  sectionId: string;
  findVideoByEncodedId: (id: string) => PlaylistVideoLike | undefined;
  getVideoElement: (id: string) => HTMLVideoElement | null;
  getOverlayElement: (id: string) => HTMLElement | null;
  safelyPauseAndResetVideo: (videoElement: HTMLVideoElement) => void;
  safelyToggleOverlay: (overlay: HTMLElement, isVisible: boolean) => void;
}): void {
  const {
    videoEncodedId,
    sectionId,
    getVideoElement,
    getOverlayElement,
    safelyPauseAndResetVideo,
    safelyToggleOverlay,
  } = params;

  const videoElement = getVideoElement(`${sectionId}-trailer-${videoEncodedId}`);
  if (videoElement) {
    safelyPauseAndResetVideo(videoElement);
  }
  const overlay = getOverlayElement(`${sectionId}-overlay-${videoEncodedId}`);
  if (overlay) {
    safelyToggleOverlay(overlay, true);
  }
}

export function handleThumbnailClickPlayList(params: {
  videoEncodedId: string;
  title: string;
  sectionId: string;
  findVideoByEncodedId: (id: string) => PlaylistVideoLike | undefined;
  getVideoElement: (id: string) => HTMLVideoElement | null;
  safelyInvokeFireworkPlayer: (videoEncodedId: string) => void;
}): void {
  const {
    videoEncodedId,
    title,
    sectionId,
    findVideoByEncodedId,
    getVideoElement,
    safelyInvokeFireworkPlayer,
  } = params;

  const video = findVideoByEncodedId(videoEncodedId);

  console.log("[UnifiedMediaSection] playlist/category thumbnail click", video);

  if (video) {
    const categoryFromVideo =
      (typeof video.event_description === "string" && video.event_description.trim()) ||
      (typeof video.category === "string" && video.category.trim()) ||
      "NA";

    const data = {
      section_title: title,
      component: componentNameConstants.VIDEO_GRID,
      event: dlEventConstants.THUMBNAIL_CLICK,
      element: dlEventConstants.THUMBNAIL_CLICK,
      cta_text: video.event_name || video.caption || "Explore Now",
    };

    const videoId = videoEncodedId || "NA";
    const cleverTapObject = {
      EVENT_NAME: livelandingPageEventNames.LIVE_COMMERCE_PAGE_CLICKED,
      EP_COMPONENT_NAME: livelandingPageComponents.VIDEO_CARD,
      EP_CLICK_TYPE: livelandingPageClickTypes.VIDEO_CARD_BUTTON_CLICK,
      EP_REFERENCE_ID: livelandingPageReferenceIds.RECENT_LIVE_VIDEOS,
      EP_PROPERTY_TYPE: livelandingPagePropertyTypes.CLP,
      EP_SECTION: title,
      EP_CTA_TEXT: video.event_name || "Explore Now",
      EP_EVENT_TYPE: livelandingPageEventTypes.CLICK,
      EP_JOURNEY: "LIVE_COMMERCE",
      EP_JOURNEY_NAME: livelandingPageJourneyNames.LIVE_LANDING,
      EP_VIDEO_TITLE: video.event_name || "NA",
      EP_LIVEVIDEO_IMPRESSION: videoId,
      EP_ID: videoId,
      EP_LIVE_SESSION: video.status === "active" ? "ACTIVE" : "INACTIVE",
      EP_POSITION: "1",
      EP_SR_NUMBER: "1",
      EP_SEGMENT: "LIVE_COMMERCE",
      EP_SPRINT: "DPAOP3",
      EP_NUDGE: "NA",
      EP_PRODUCT_NAME: categoryFromVideo,
      EP_PRODUCT_CATEGORY: `${categoryFromVideo || "NA"} || ${videoId}`,
      EP_SEARCH: getSearchTerm(),
      EP_SOURCE: "live-landing-page",
      EP_LANG: "eng",
      EP_SUB_SECTION: getSubsectionFromHashtags(video?.trailer?.hashtags),
    };

    dataLayer("", data, cleverTapObject);
  }

  const videoElement = getVideoElement(`${sectionId}-trailer-${videoEncodedId}`);
  if (videoElement) videoElement.pause();

  safelyInvokeFireworkPlayer(videoEncodedId);
}

export function querySelectorIncludingOpenShadowRoots(
  selector: string,
  root: ParentNode = document
): Element | null {
  const directMatch = root.querySelector(selector);
  if (directMatch) return directMatch;

  const elements = root.querySelectorAll("*");
  for (const element of elements) {
    const shadowRoot = (element as HTMLElement & { shadowRoot?: ShadowRoot }).shadowRoot;
    if (!shadowRoot) continue;

    const nestedMatch = querySelectorIncludingOpenShadowRoots(selector, shadowRoot);
    if (nestedMatch) return nestedMatch;
  }

  return null;
}

export function findStoryBlockElement(encodedId: string | undefined) {
  const cardRoot = encodedId ? document.getElementById(encodedId) : null;

  if (cardRoot) {
    const cardScopedSelectors = [
      `fw-storyblock[name="player-${encodedId}"]`,
      `fw-storyblock[video="${encodedId}"]`,
      'fw-storyblock[data-testid="video-player"]',
      "fw-storyblock",
    ];

    for (const selector of cardScopedSelectors) {
      const found = querySelectorIncludingOpenShadowRoots(selector, cardRoot) as
        (HTMLElement & { shadowRoot?: ShadowRoot }) | null;
      if (found) return found;
    }
  }

  const selectors: string[] = [];
  if (encodedId) {
    selectors.push(`fw-storyblock[name="player-${encodedId}"]`);
    selectors.push(`fw-storyblock[video="${encodedId}"]`);
  }
  selectors.push('fw-storyblock[data-testid="video-player"]');
  selectors.push("fw-storyblock");

  for (const selector of selectors) {
    const found = querySelectorIncludingOpenShadowRoots(selector) as
      (HTMLElement & { shadowRoot?: ShadowRoot }) | null;
    if (found) return found;
  }

  return null;
}

export function setStoryBlockPipMessageZIndex(encodedId: string | undefined, maxRetries = 8): void {
  const trySetZIndex = (attempt: number) => {
    const storyBlock = findStoryBlockElement(encodedId);
    const pipMessage = storyBlock?.shadowRoot?.querySelector(
      ".cy-video-in-pip-message"
    ) as HTMLElement | null;

    if (!pipMessage?.parentElement) {
      if (attempt < maxRetries) {
        requestAnimationFrame(() => trySetZIndex(attempt + 1));
      }
      return;
    }

    pipMessage.parentElement.style.zIndex = "4";
  };

  trySetZIndex(0);
}

export function playMutedPreview(videoElement: HTMLVideoElement | null): void {
  if (!videoElement) return;

  videoElement.muted = true;
  videoElement.defaultMuted = true;
  Promise.resolve(videoElement.play()).catch(() => {
    // ignore autoplay failures
  });
}

export function stopPreview(videoElement: HTMLVideoElement | null): void {
  if (!videoElement) return;

  videoElement.pause();
  videoElement.currentTime = 0;
}

export function hasUnmuteMethod(_player: unknown): undefined {
  return undefined;
}
